// Settings panel for the user-self-toggleable capabilities.
//
// Slice RP-2b-ii of docs/planning/firefly_role_context_presentation.md
// §10. Mirrors the assist-level section shape
// (`SettingsDrawerActivityDockSection`): pure presentational shell;
// storage / subscriptions live in `useUserCapabilitiesEditor` which
// the calling popover already runs.
//
// One row per capability (today: just `is_ads_runner`). Each row is
// a labelled checkbox: checked iff the capability is **effective**
// (explicit override OR role-derived default). Clicking writes an
// explicit override matching the new state — even when it equals
// the role-derived default, so a user who deliberately flipped the
// toggle keeps their preference if their role changes later. A
// "Default for your role" hint appears when no explicit override
// is in storage.
//
// Intentionally NOT in this slice:
//   - A "reset to role default" affordance. The user can re-check
//     the box to land on the matching effective value; clearing the
//     override is an implementation detail surfaced only via tests.
//     Add a reset link later if pilot users complain about not
//     being able to "go back to default" — keep the v1 surface
//     small.
//   - Per-bucket / per-mode capability hints. The blurb is one
//     sentence per capability; bucket-aware copy can land alongside
//     the eventual bucket-axis render-context work.

import type {
  Capability,
  CapabilityDescription,
} from "@/lib/capabilities/capabilities";
import type { CapabilityOverrides } from "@/lib/capabilities/capabilityStorage";

export type SettingsDrawerCapabilitiesSectionProps = {
  /** Ordered list of capabilities to render. Caller supplies (the
   * popover passes the canonical `CAPABILITY_DESCRIPTIONS`); the
   * section is agnostic to which subset it's asked to render. */
  capabilities: readonly CapabilityDescription[];
  /** Current override snapshot from `useUserCapabilitiesEditor`. Used
   * to detect "no preference (using default)" so the explainer
   * hint can render alongside the row. */
  overrides: CapabilityOverrides;
  /** Effective state probe: explicit override or role-derived default. */
  isEffective: (capability: Capability) => boolean;
  /** Override writer. The section always writes an explicit boolean
   * (matching the toggle's new state); the editor hook's `null`
   * channel is reserved for a future "reset to default" affordance. */
  setOverride: (capability: Capability, value: boolean | null) => void;
};

export function SettingsDrawerCapabilitiesSection({
  capabilities,
  overrides,
  isEffective,
  setOverride,
}: SettingsDrawerCapabilitiesSectionProps) {
  return (
    <section className="space-y-4">
      <h3 className="text-xs font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400">
        Responsibilities
      </h3>

      <fieldset>
        <legend className="sr-only">Tasks you do for your organization</legend>
        <div className="space-y-2" role="group" aria-label="Responsibilities">
          {capabilities.map((cap) => {
            const effective = isEffective(cap.id);
            const hasExplicitOverride = overrides[cap.id] !== undefined;
            return (
              <label
                key={cap.id}
                className={
                  "flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition-colors " +
                  (effective
                    ? "border-indigo-300 bg-indigo-50 dark:border-indigo-700 dark:bg-indigo-950/50"
                    : "border-gray-200 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-zinc-800/60")
                }
              >
                <input
                  type="checkbox"
                  name={`capability-${cap.id}`}
                  checked={effective}
                  onChange={(e) => setOverride(cap.id, e.target.checked)}
                  className="mt-0.5 h-4 w-4 shrink-0 accent-indigo-600 dark:accent-indigo-400"
                />
                <span className="block">
                  <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">
                    {cap.label}
                  </span>
                  <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">
                    {cap.blurb}
                  </span>
                  {!hasExplicitOverride ? (
                    <span className="mt-1 block text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500">
                      Default for your role
                    </span>
                  ) : null}
                </span>
              </label>
            );
          })}
        </div>
      </fieldset>

      <p className="text-[11px] leading-snug text-gray-500 dark:text-gray-400">
        These settings tailor the daily checklist, achievements, and dock
        reminders to the work you actually do. They only affect your own view
        — admins manage org-wide responsibilities from Members.
      </p>
    </section>
  );
}
